import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/icon';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/auth/auth-context';
import { persistLang } from '@/i18n/i18n';
import { type Theme, getStoredTheme, persistTheme } from '@/lib/theme';

type Locale = 'fr' | 'en';

function roleLabel(role: string, t: (key: string) => string): string {
  switch (role) {
    case 'admin':
      return t('role_admin');
    case 'operator':
      return t('role_operator');
    case 'auditor':
      return t('role_auditor');
    default:
      return role;
  }
}

export function UserMenu(): JSX.Element {
  const { t, i18n } = useTranslation(['common', 'auth']);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleLocaleChange = useCallback(
    (next: Locale) => {
      void i18n.changeLanguage(next).then(() => persistLang(next));
      // Close the menu after changing locale so the subsequent click on the
      // trigger reliably re-opens it (the trigger toggles open state, so
      // leaving the menu open would cause the next click to close it instead
      // of opening it — breaking the shell-login locale-switch flow).
      setOpen(false);
    },
    [i18n],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const currentLang: Locale = i18n.language === 'en' ? 'en' : 'fr';

  const [currentTheme, setCurrentTheme] = useState<Theme>(getStoredTheme);

  const handleThemeChange = useCallback((next: Theme) => {
    setCurrentTheme(next);
    persistTheme(next);
  }, []);

  if (!currentUser) return <></>;

  return (
    <div className="relative" ref={ref}>
      {/* Trigger button: carries current-user testid so auth tests can find
          the display name and role badges without opening the dropdown. */}
      <button
        type="button"
        data-testid="topbar-user-menu"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted"
      >
        <Icon name="user" size={16} />
        <div data-testid="current-user" className="flex items-center gap-2">
          <span data-testid="topbar-user-name">{currentUser.display_name}</span>
          <ul className="flex gap-1" aria-label={t('user_menu')}>
            {currentUser.roles.map((role: string) => (
              <li
                key={role}
                className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground"
                data-testid={`role-badge-${role}`}
              >
                {roleLabel(role, (key) => t(key))}
              </li>
            ))}
          </ul>
        </div>
        <Icon name="chevron-down" size={14} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 rounded-md border border-border bg-card p-2 shadow-lg z-30"
        >
          <div className="border-b border-border py-2 px-2">
            <label htmlFor="locale-switcher-menu" className="block text-xs text-muted-foreground">
              {t('language')}
            </label>
            {/* Kit Select rendered NON-portalled: the menu's outside-click
                handler (mousedown on window) closes on any click outside
                `ref` — a portalled listbox lives under document.body, so
                picking an option would close the menu before the value
                lands. Inline content keeps the options inside `ref`. */}
            <Select
              value={currentLang}
              onValueChange={(v) => handleLocaleChange(v as Locale)}
            >
              <SelectTrigger
                id="locale-switcher-menu"
                data-testid="locale-switcher"
                className="mt-1 h-9"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent portalled={false}>
                <SelectItem value="fr">{t('language_french')}</SelectItem>
                <SelectItem value="en">{t('language_english')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="border-b border-border py-2 px-2" data-testid="theme-toggle">
            <span className="block text-xs text-muted-foreground">
              {t('theme_label')}
            </span>
            <div className="mt-1 flex gap-1">
              {(['light', 'dark', 'system'] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  data-testid={`theme-option-${opt}`}
                  onClick={() => handleThemeChange(opt)}
                  className={[
                    'flex-1 rounded-md border px-2 py-1 text-xs transition-colors',
                    currentTheme === opt
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input bg-background text-muted-foreground hover:bg-muted',
                  ].join(' ')}
                >
                  {opt === 'light' ? t('theme_light') : opt === 'dark' ? t('theme_dark') : t('theme_system')}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              void handleLogout();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted"
          >
            <Icon name="log-out" size={16} />
            {t('sign_out', { ns: 'auth' })}
          </button>
        </div>
      )}
    </div>
  );
}
